import { Link } from "@/base/component/link";
import { Card, CardBody, CardHeader } from "@heroui/card";
import {
  IconBrandGoogle,
  IconBrandOpenai,
  IconChartBar,
  IconCode,
} from "@tabler/icons-react";

export default function Integration() {
  return (
    <section className="py-16 text-center" id="integration">
      <div className="text-center mb-12 px-4">
        <h2 className="font-bold mb-4 text-3xl md:text-5xl">
          Powered by leading SEO technologies
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-lg md:text-xl">
          Get ahead in search rankings with our powerful combination of trusted
          SEO tools and AI analysis. We deliver actionable insights that drive
          real results.
        </p>
      </div>

      <div className="px-4 md:container md:mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Analysis Tools */}
          <IntegrationItem
            title="Analysis tools"
            description="Essential tools that power your SEO success"
            integrations={[
              {
                icon: (
                  <IconBrandGoogle
                    className="w-8 h-8"
                    aria-label="Google PageSpeed Insights logo"
                  />
                ),
                name: (
                  <Link href="https://pagespeed.web.dev/" target="_blank">
                    PageSpeed Insights
                  </Link>
                ),
                description:
                  "Boost your search rankings with Google's official speed metrics and core web vitals analysis",
              },
              {
                icon: (
                  <IconChartBar
                    className="w-8 h-8"
                    aria-label="DataForSEO analytics chart icon"
                  />
                ),
                name: (
                  <Link href="https://dataforseo.com/" target="_blank">
                    DataForSEO
                  </Link>
                ),
                description:
                  "Get accurate website metrics, and discover high-impact SEO opportunities",
              },
            ]}
          />

          {/* AI & Processing */}
          <IntegrationItem
            title="AI & processing"
            description="Smart analysis for data-driven SEO decisions"
            integrations={[
              {
                icon: (
                  <IconBrandOpenai
                    className="w-8 h-8"
                    aria-label="OpenAI logo"
                  />
                ),
                name: (
                  <Link href="https://openai.com" target="_blank">
                    OpenAI
                  </Link>
                ),
                description:
                  "Generate optimized content and meta descriptions that rank higher in search results",
              },
              {
                icon: (
                  <IconCode
                    className="w-8 h-8"
                    aria-label="Google Gemini code analysis icon"
                  />
                ),
                name: (
                  <Link href="https://gemini.google.com/" target="_blank">
                    Google Gemini
                  </Link>
                ),
                description:
                  "Deep content analysis to ensure your pages match search intent and ranking factors",
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function IntegrationItem({
  title,
  description,
  integrations,
}: {
  title: string;
  description: string;
  integrations: {
    icon: React.ReactNode;
    name: React.ReactNode;
    description: React.ReactNode;
  }[];
}) {
  return (
    <Card className="p-4">
      <CardHeader>
        <h3 className="text-xl font-semibold">{title}</h3>
      </CardHeader>
      <CardBody>
        <p className="mb-6">{description}</p>

        <ul className="space-y-6">
          {integrations.map((integration, index) => (
            <li key={index} className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                {integration.icon}
              </div>
              <div>
                <p className="font-medium">{integration.name}</p>
                <p className="text-sm ">{integration.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
